import { Component, Vue, Watch } from 'vue-property-decorator';
import { videoPlayer } from 'vue-video-player';
import { VideoJsPlayerOptions } from 'video.js';
import { Getter } from 'vuex-class';
import VideoItem from '@/common/interfaces/VideoItem';
import SizeBreakpoints from '@/common/interfaces/SizeBreakpoints';
import { isDesktopSafari, isPhone, isSSR } from '@/utils/commonUtils';

if (!isSSR()) {
  require('videojs-youtube')
  require('videojs-vimeo')
}

import 'video.js/dist/video-js.css';
import { SegmentElement } from '@/plugins/utm/SegmentElement';

const VideoPlayerProps = Vue.extend({
  props: {
    video: Object as () => VideoItem,
    options: Object as () => VideoJsPlayerOptions,
    fluid: Boolean,
    isActiveSlide: Boolean,
    modalAutoPlay: Boolean,
    externalStop: Boolean,
    noAnalytics: Boolean,
    controls: {
      type: Array as () => string[],
      default: () => ['nodownload'],
    },
  },
});

@Component({
  components: {
    videoPlayer,
  },
})
export default class VideoPlayer extends VideoPlayerProps {
  playing = false;
  wasAutoplayInitiated = false;
  playingInterval: number;

  minification = '?width=1500&height=910&mode=crop&scale=none&quality=45'

  intersectionOptions = {
    root: null,
    rootMargin: '0px 0px 0px 0px',
    threshold: [0, 1],
  };

  @Getter isInBreakpoint!: (breakpoints: SizeBreakpoints[] | SizeBreakpoints) => boolean;

  get player() {
    return (this.$refs.videoPlayer as any).player;
  }

  @Watch('externalStop')
  onExternalStop(active: boolean) {
    if (!active) this.resetPlayer()
  }

  getIsFullScreen() {
    return this.player.isFullscreen()
  }

  getSound() {
    return this.player.muted() ? 0 : Math.floor(this.player.volume() * 100);
  }

  @Watch('playing')
  playingChanged() {
    if (!this.noAnalytics) {
      if (this.playing && this.modalAutoPlay && !this.wasAutoplayInitiated) {
        this.wasAutoplayInitiated = true;
        this.setPlayingInterval();
      } else if (this.playing) {
        this.setPlayingInterval();
      }
      if (!this.playing && this.playingInterval) {
        clearInterval(this.playingInterval);
        this.playingInterval = null;
      }
    }
  }

  beforeDestroy() {
    this.resetPlayer();
  }

  get playerType() {
    return this.video.Type.includes('youtube') ? 'youtube' : 'vimeo';
  }

  get combinedOptions() {
    const fromVideoItem: VideoJsPlayerOptions = {
      sources: [
        {
          type: this.video.Type,
          src: this.video.Url,
        },
      ],
      poster: this.video.Poster + this.minification,
    };

    const defaultOptions: VideoJsPlayerOptions = {
      techOrder: ['vimeo', 'youtube', 'html5'],
      autoplay: false,
      loop: false,
      muted: false,
      controls: true,
      language: 'en',
      poster: '',
      preload: 'none',
      controlBar: {
        //@ts-ignore
        fullscreenToggle: !!(document.cancelFullScreen || document.mozCancelFullScreen || document.webkitCancelFullScreen)
      }
    };

    return {
      ...defaultOptions,
      ...this.options,
      ...fromVideoItem,
    };
  }

  get isSafari() {
    return isDesktopSafari() || isPhone();
  }

  handleButtonClick() {
    // if playing, pause
    if (this.playing) this.player.pause();
  }
  externalClose() {
    this.playing = false;
  }

  setControlsList(): void {
    const player = this.player.el_;

    if (!player) {
      return;
    }

    const videoEl = player.querySelector('video');
    const controls = this.controls.join(' ');

    videoEl.setAttribute('controlsList', controls);
  }

  onPlayerPlay() {
    this.playing = true;

    this.$emit('player-playing', this.playing);
    this.onPlayerStarted();
  }

  onPlayerStarted() {
    this.$nextTick(() => {
      setTimeout(() => {
        SegmentElement.sendEvent('videoPlaybackStarted', {
          full_screen: this.getIsFullScreen(),
          position: Math.floor(this.player.currentTime()),
          sound: this.getSound(),
          total_length: Math.floor(this.player.duration()),
          video_player: this.playerType,
          asset_source: this.player.currentSrc(),
        }, true)
      }, 1200)
    })
  }

  setPlayingInterval() {
    if (!this.playingInterval) {
      this.playingInterval = setInterval(this.onPlayerPlaying, 10000);
    }
  }

  onPlayerPlaying() {
    const pos = Math.floor(this.player.currentTime());
    SegmentElement.sendEvent('videoContentPlaying', {
      full_screen: this.getIsFullScreen(),
      position: Math.ceil(pos / 10) * 10,
      sound: this.getSound(),
      total_length: Math.floor(this.player.duration()),
      video_player: this.playerType,
      asset_source: this.player.currentSrc(),
    }, true)
  }

  onPlayerPause() {
    this.playing = false;

    this.$emit('player-paused', this.playing);
  }

  onPlayerEnded() {
    this.playing = false;

    this.$emit('player-ended', this.playing);
    const total_length = Math.floor(this.player.duration());
    SegmentElement.sendEvent('videoPlaybackCompleted', {
      full_screen: this.getIsFullScreen(),
      position: total_length,
      sound: this.getSound(),
      total_length,
      video_player: this.playerType,
      asset_source: this.player.currentSrc(),
    })
  }

  onWaypoint(el: any) {
    if (this.isInBreakpoint('md')) {
      if (el.going === 'out') {
        this.player.pause();
      } else {
        if (el.going === 'in' && this.options.autoplay === true) {
          this.player.play();
        }
      }
    }
  }

  resetPlayer() {
    this.player.pause();
    this.player.currentTime(0);
    this.player.hasStarted(false);
    if (this.playingInterval) {
      clearInterval(this.playingInterval);
      this.playingInterval = null;
    }
  }

  fixBtnTooltipAudioEye() {
    const videoBlock = (this.$refs.videoPlayer as any).$el;
    videoBlock.querySelectorAll('button[title]').forEach((btn: HTMLButtonElement) => {
      btn.removeAttribute('title')
    })
    setTimeout(() => {
      videoBlock.querySelector('ul.vjs-menu-content')?.removeAttribute('role')
      videoBlock.querySelectorAll('li.vjs-menu-item').forEach((li: HTMLElement) => {
        li.removeAttribute('role')
      })
    }, 2400)
  }

  fixListOneItemAudioEye() {
    const videoBlock = (this.$refs.videoPlayer as any).$el;
    const vjsMenuContent = videoBlock.querySelectorAll('ul.vjs-menu-content')
    if (vjsMenuContent.length) {
      vjsMenuContent.forEach((el: HTMLUListElement) => {
        if (el.childNodes.length < 2) {
          el.remove()
        }
      })
    }
  }

  mounted() {
    this.fixBtnTooltipAudioEye();

    if (this.modalAutoPlay && !this.player.autoplay()) {
      this.player.play();
    }
    this.player.poster(this.video.Poster + this.minification);

    const playContent =
      '<svg class="video-player__svg" aria-hidden="true" viewBox="0 0 206 206" xmlns="http://www.w3.org/2000/svg"><circle cx="102" cy="102" r="102" stroke-dasharray="1000" stroke-dashoffset=1000 stroke="#FFF" fill="none" fill-rule="evenodd" /></svg>';

    const playButtons = Array.prototype.slice.call(
      document.querySelectorAll('.vjs-big-play-button'),
      0,
    );

    playButtons.forEach((playButton) => {
      // workaround as SVG pause button added with CSS wasn't allowing the video to pause
      // add click event and handler
      playButton.addEventListener('click', this.handleButtonClick);

      playButton.innerHTML += playContent;

      if (playButton.querySelectorAll('.video-player__svg').length > 1) {
        const svg = playButton.querySelector('.video-player__svg');

        if (svg) {
          svg.remove();
        }
      }
    });

    this.setControlsList();

    setTimeout(() => {
      this.fixListOneItemAudioEye();
    }, 1500)
  }
}
